import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { useReportStore } from '../../store/reports/reportStore';
import useSchoolStore from '../../store/schoolStore';
import ChatMessage from '../../components/chat/ChatMessage';
import { LuArrowLeft, LuBookOpen, LuFileX } from 'react-icons/lu';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import EmptyState from '../../components/common/EmptyState';
import LoadingMessage from '../../components/common/LoadingMessage';

export default function StudentReportSubjectPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const routeParams = useParams();

    const { schoolId: storeSchoolId, currentAcademicYear: storeAcademicYear, schoolData } = useSchoolStore();
    const schoolId = routeParams.schoolId || searchParams.get('schoolId') || storeSchoolId;
    const academicYear = location.state?.academicYear || storeAcademicYear || schoolData?.currentAcademicYear || '';

    const urlClass = searchParams.get('class') || location.state?.selectedClass || '';
    const urlSec = searchParams.get('sec') || location.state?.selectedSec || '';
    const urlUserId = searchParams.get('userId') || location.state?.userId || '';
    const urlSubjectName = searchParams.get('subject') || location.state?.subjectName || '';

    const { 
        studentReport, 
        isLoadingStudentReport, 
        getStudentReport 
    } = useReportStore();

    // Scroll to top immediately on mount
    useEffect(() => {
        const scrollContainer = document.getElementById('dashboard-main-content');
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    // If navigated without preloaded state, fetch student report from Firestore
    useEffect(() => {
        if (!location.state?.subject && urlUserId && schoolId) {
            getStudentReport({
                schoolId,
                userId: urlUserId,
                Class: urlClass,
                sec: urlSec,
                academicYear
            });
        }
    }, [location.state, urlUserId, schoolId, urlClass, urlSec, academicYear, getStudentReport]);

    // Resolve the active subject object
    const subject = useMemo(() => {
        if (location.state?.subject) {
            return location.state.subject;
        }

        const reportContent = studentReport?.report;
        if (reportContent && Array.isArray(reportContent.subjectPerformance)) {
            if (urlSubjectName) {
                const found = reportContent.subjectPerformance.find(
                    subjectItem => (subjectItem.subjectName || '').toLowerCase() === urlSubjectName.toLowerCase()
                );
                if (found) return found;
            }
            return reportContent.subjectPerformance[0];
        }

        return null;
    }, [location.state, studentReport, urlSubjectName]);

    const studentName = location.state?.studentName || studentReport?.name || 'Student';

    const handleGoBack = () => {
        navigate(-1);
    };

    const subjectTitle = subject?.subjectName || urlSubjectName || 'Subject';
    const details = [studentName];
    if (urlClass) details.push(`Class ${urlClass} ${urlSec}`);
    if (academicYear) details.push(academicYear);

    const renderBody = () => {
        if (isLoadingStudentReport && !subject) {
            return <LoadingMessage message="Loading the subject report…" />;
        }

        if (!subject) {
            return (
                <EmptyState
                    icon={<LuFileX />}
                    title="Subject report not found"
                    message={`There are no details for ${urlSubjectName || 'this subject'} in this student’s report.`}
                    action={<Button variant="secondary" onClick={handleGoBack}>Back to the student’s report</Button>}
                />
            );
        }

        if (!subject.detailedReport) {
            return <EmptyState icon={<LuBookOpen />} title="No details for this subject" message="The report has no chapter-wise details for this subject." />;
        }

        return (
            <ChatMessage
                message={{
                    id: `subject-report-${subject.subjectId || subject.subjectName || 'subject'}`,
                    content: subject.detailedReport,
                    role: 'assistant'
                }}
                fullWidth
                isPlain
                showPdfExport={false}
            />
        );
    };

    const page = (
        <div className="mx-auto max-w-5xl">
            <div className="mb-1.5">
                <button
                    type="button"
                    onClick={handleGoBack}
                    className="inline-flex items-center gap-1.5 rounded text-[13px] font-medium text-ui-ink-2 transition-colors hover:text-ui-ink"
                >
                    <LuArrowLeft aria-hidden className="size-4" />
                    {studentName}’s report
                </button>
            </div>
            <PageHeader title={`${subjectTitle} report`} subtitle={details.join(' · ')} />
            <Card>{renderBody()}</Card>
        </div>
    );

    // Inside the dashboard the shell gives the page its padding; a shared link needs its own
    if (routeParams.schoolId) {
        return page;
    }

    return <div className="min-h-screen bg-ui-page px-8 py-6 text-ui-ink">{page}</div>;
}
